// mimberTheme.ts
export const MIMBER_COLORS = {
  navy: "#0E3A5D",
  azure: "#2DA4E0",

  // Light neutrals
  bgLayout: "#F4F7FA",
  text: "#0E3A5D",
  textSecondary: "#5B6B7A",
  border: "#D6DEE6",

  // Dark neutrals (navy-based)
  darkBgLayout: "#081A2A",
  darkBgContainer: "#0B2236",
  darkBgElevated: "#0E2A42",
  darkBorder: "#12324D",
  darkText: "#E6EEF5",
  darkTextSecondary: "#A8B6C4",

  // Status
  success: "#2FBF71",
  warning: "#F5A623",
  error: "#E5484D",
} as const

// Shared theme configuration values for Mimber apps
export const themeTokens = {
  // Brand/action colors
  colorPrimary: MIMBER_COLORS.azure,
  colorInfo: MIMBER_COLORS.azure,

  // Status colors
  colorSuccess: MIMBER_COLORS.success,
  colorWarning: MIMBER_COLORS.warning,
  colorError: MIMBER_COLORS.error,

  // Typography
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: 14,

  // Shape
  borderRadius: 12,
  controlHeight: 36,

  // Layout style
  wireframe: false,

  // Light backgrounds
  colorBgContainer: "#FFFFFF",
  colorBgElevated: "#FFFFFF",
  colorBgLayout: MIMBER_COLORS.bgLayout,

  // Light text / borders
  colorText: MIMBER_COLORS.text,
  colorTextSecondary: MIMBER_COLORS.textSecondary,
  colorBorder: MIMBER_COLORS.border,

  // Focus ring
  controlOutlineWidth: 2,
  controlOutline: "rgba(45, 164, 224, 0.28)",

  // Link styling
  colorLink: MIMBER_COLORS.azure,
  colorLinkHover: "#1B8FD4",
} as const

// Dark theme specific token overrides
export const darkThemeTokens = {
  ...themeTokens,

  // Keep Azure as action color in dark mode
  colorPrimary: MIMBER_COLORS.azure,
  colorInfo: MIMBER_COLORS.azure,

  // Dark backgrounds (navy-based, not pure black)
  colorBgLayout: MIMBER_COLORS.darkBgLayout,
  colorBgContainer: MIMBER_COLORS.darkBgContainer,
  colorBgElevated: MIMBER_COLORS.darkBgElevated,

  // Dark text / borders
  colorText: MIMBER_COLORS.darkText,
  colorTextSecondary: MIMBER_COLORS.darkTextSecondary,
  colorBorder: MIMBER_COLORS.darkBorder,
  colorBorderSecondary: MIMBER_COLORS.darkBorder, // Use navy-based border instead of gray

  // Focus ring (slightly stronger for dark)
  controlOutline: "rgba(45, 164, 224, 0.40)",

  // Status tuned for dark
  colorSuccess: "#3DD68C",
  colorWarning: "#F7B84B",
  colorError: "#F97066",
} as const

export const componentTokens = {
  // Buttons
  Button: {
    borderRadius: 10,
    controlHeight: 36,
    defaultBorderColor: MIMBER_COLORS.border,
    defaultHoverBorderColor: "rgba(45, 164, 224, 0.55)",
    defaultHoverColor: MIMBER_COLORS.azure,
  },

  // Inputs (Input, Select, DatePicker, etc.)
  Input: {
    borderRadius: 10,
    hoverBorderColor: "rgba(45, 164, 224, 0.55)",
    activeBorderColor: MIMBER_COLORS.azure,
    activeShadow: "0 0 0 2px rgba(45, 164, 224, 0.20)",
  },
  Select: {
    borderRadius: 10,
    optionSelectedBg: "rgba(45, 164, 224, 0.14)",
    optionActiveBg: "rgba(45, 164, 224, 0.10)",
  },

  // Forms
  Form: {
    labelColor: MIMBER_COLORS.text,
    labelRequiredMarkColor: MIMBER_COLORS.error,
    itemMarginBottom: 16,
  },

  // Table (admin UI polish)
  Table: {
    borderRadius: 12,
    headerBg: "#F7FAFD",
    headerColor: MIMBER_COLORS.text,
    rowHoverBg: "rgba(45, 164, 224, 0.06)",
    rowSelectedBg: "rgba(45, 164, 224, 0.12)",
    rowSelectedHoverBg: "rgba(45, 164, 224, 0.16)",
  },

  // Modal / Drawer
  Modal: {
    borderRadius: 16,
    headerBg: "#FFFFFF",
    contentBg: "#FFFFFF",
    titleColor: MIMBER_COLORS.text,
  },
  Drawer: {
    colorBgElevated: "#FFFFFF",
  },

  // Cards
  Card: {
    borderRadius: 16,
    headerBg: "#FFFFFF",
  },

  // Tabs
  Tabs: {
    inkBarColor: MIMBER_COLORS.azure,
    itemSelectedColor: MIMBER_COLORS.azure,
    itemHoverColor: "#1B8FD4",
  },

  // Menu / Layout (shell)
  Layout: {
    headerBg: "#FFFFFF",
    bodyBg: MIMBER_COLORS.bgLayout,
    siderBg: "#FFFFFF",
  },
  Menu: {
    itemBg: "transparent",
    itemColor: MIMBER_COLORS.text,
    itemHoverBg: "rgba(45, 164, 224, 0.12)",
    itemSelectedBg: MIMBER_COLORS.azure,
    itemSelectedColor: "#FFFFFF",
  },

  // Notifications / Alerts
  Alert: {
    borderRadius: 12,
  },
  Message: {
    borderRadius: 12,
  },
  Notification: {
    borderRadius: 14,
  },

  Tag: {
    borderRadius: 999, // pill style
    fontWeightStrong: 500,

    // Success
    successBg: "rgba(47, 191, 113, 0.14)",
    successColor: "#1F9D63",

    // Info (brand-aligned)
    infoBg: "rgba(45, 164, 224, 0.14)",
    infoColor: "#1B8FD4",

    // Warning
    warningBg: "rgba(245, 166, 35, 0.18)",
    warningColor: "#C97A10",

    // Error
    errorBg: "rgba(229, 72, 77, 0.16)",
    errorColor: "#C93A40",
  },
} as const

export const darkComponentTokens = {
  ...componentTokens,

  Layout: {
    headerBg: MIMBER_COLORS.darkBgElevated,
    bodyBg: MIMBER_COLORS.darkBgLayout,
    siderBg: MIMBER_COLORS.darkBgContainer,
  },

  Menu: {
    itemBg: "transparent",
    itemColor: MIMBER_COLORS.darkText,
    itemHoverBg: "rgba(45, 164, 224, 0.18)",
    itemSelectedBg: MIMBER_COLORS.azure,
    itemSelectedColor: "#FFFFFF",

    // Legacy dark menu props (kept aligned)
    darkItemBg: MIMBER_COLORS.darkBgContainer,
    darkSubMenuItemBg: MIMBER_COLORS.darkBgElevated,
    darkItemColor: MIMBER_COLORS.darkText,
    darkItemHoverBg: "rgba(45, 164, 224, 0.18)",
    darkItemSelectedBg: MIMBER_COLORS.azure,
  },

  Table: {
    borderRadius: 12,
    headerBg: "#0E2A42",
    headerColor: MIMBER_COLORS.darkText,
    rowHoverBg: "rgba(45, 164, 224, 0.10)",
    rowSelectedBg: "rgba(45, 164, 224, 0.18)",
    rowSelectedHoverBg: "rgba(45, 164, 224, 0.22)",
  },

  Modal: {
    borderRadius: 16,
    headerBg: MIMBER_COLORS.darkBgElevated,
    contentBg: MIMBER_COLORS.darkBgElevated,
    titleColor: MIMBER_COLORS.darkText,
  },

  Drawer: {
    colorBgElevated: MIMBER_COLORS.darkBgElevated,
  },

  Card: {
    borderRadius: 16,
    headerBg: MIMBER_COLORS.darkBgElevated,
  },

  Form: {
    labelColor: MIMBER_COLORS.darkText,
    labelRequiredMarkColor: "#F97066",
    itemMarginBottom: 16,
  },

  Input: {
    borderRadius: 10,
    hoverBorderColor: "rgba(45, 164, 224, 0.60)",
    activeBorderColor: MIMBER_COLORS.azure,
    activeShadow: "0 0 0 2px rgba(45, 164, 224, 0.28)",
  },
  Select: {
    borderRadius: 10,
    optionSelectedBg: "rgba(45, 164, 224, 0.18)",
    optionActiveBg: "rgba(45, 164, 224, 0.12)",
  },

  Tag: {
    borderRadius: 999,
    fontWeightStrong: 500,

    // Success
    successBg: "rgba(47, 191, 113, 0.22)",
    successColor: "#3DD68C",

    // Info (Azure-aligned)
    infoBg: "rgba(45, 164, 224, 0.22)",
    infoColor: "#7AC8F2",

    // Warning
    warningBg: "rgba(247, 184, 75, 0.22)",
    warningColor: "#F7B84B",

    // Error
    errorBg: "rgba(249, 112, 102, 0.22)",
    errorColor: "#F97066",
  },
} as const
